import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Plane, ArrowUpRight, Sparkles, MapPin } from "lucide-react";

const STATUS_PILL = {
  Submitted: "as-pill-blue", "Under Review": "as-pill-blue",
  "In Planning": "as-pill-orange", "Awaiting Client Confirmation": "as-pill-orange",
  Confirmed: "as-pill-gold", "In Progress": "as-pill-gold",
  Completed: "as-pill-green", Cancelled: "as-pill-red", Draft: "as-pill-muted",
};

export default function PortalOverview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/stats/client").then(r => setData(r.data)); }, []);

  return (
    <DashboardLayout role="client">
      <div className="max-w-[1200px] mx-auto px-8 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="as-overline">Client Portal</div>
            <h1 className="as-serif text-5xl font-light mt-2" data-testid="portal-overview-title">Good evening, {data?.profile?.name?.split(" ")[0] || "there"}.</h1>
            <p className="text-[#756E64] mt-2 font-light">Your active missions and services.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/portal/trips/new" className="as-btn-gold px-5 py-3 rounded-sm as-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2" data-testid="new-trip-cta"><Plane size={13}/> New Trip Request</Link>
          </div>
        </div>

        {/* Upcoming Trips */}
        <section className="mb-14">
          <div className="flex items-center justify-between mb-5">
            <div className="as-overline">Upcoming Missions</div>
            <Link to="/portal/trips" className="text-xs text-[#756E64] hover:text-[#A47E2A] as-mono uppercase tracking-widest" data-testid="see-all-trips">See all</Link>
          </div>
          {data?.upcoming?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {data.upcoming.map(t => (
                <Link key={t.id} to={`/portal/trips/${t.id}`} className="as-card p-6 block group" data-testid={`upcoming-trip-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className={`as-pill ${STATUS_PILL[t.status] || "as-pill-muted"} as-mono`}>{t.status}</div>
                      <div className="as-serif text-3xl mt-3 font-light">
                        {t.legs?.[0]?.departure_airport} <span className="as-gold">→</span> {t.legs?.[0]?.arrival_airport}
                      </div>
                      <div className="text-sm text-[#756E64] mt-1 as-mono">{new Date(t.legs?.[0]?.departure_datetime).toUTCString().slice(0, 22)}</div>
                    </div>
                    <ArrowUpRight size={20} className="text-[#756E64] group-hover:as-gold transition-colors"/>
                  </div>
                  <div className="mt-4 flex gap-5 text-xs text-[#756E64] as-mono">
                    <span>PAX · {t.passenger_count}</span>
                    <span>ACFT · {t.aircraft_preference || "TBD"}</span>
                    <span>OWNER · {t.owner_name || "Unassigned"}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="as-card p-10 text-center text-[#756E64]" data-testid="portal-no-trips">
              <Plane size={28} className="mx-auto opacity-40 mb-3"/>
              <div className="as-serif text-2xl mb-2 text-[#1A1D24]">No active trips</div>
              <p className="text-sm">Submit a new trip request and your dedicated ops lead will begin planning.</p>
              <Link to="/portal/trips/new" className="as-btn-gold inline-block mt-6 px-5 py-3 rounded-sm as-mono text-[10px] uppercase tracking-[0.2em]" data-testid="empty-new-trip">Create Trip Request</Link>
            </div>
          )}
        </section>

        {/* Concierge */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="as-overline">Concierge · Live</div>
            <Link to="/portal/concierge" className="text-xs text-[#756E64] hover:text-[#A47E2A] as-mono uppercase tracking-widest" data-testid="see-concierge">See all</Link>
          </div>
          {data?.concierge?.length ? (
            <div className="as-card divide-y divide-[#E5DFD2]">
              {data.concierge.map(c => (
                <div key={c.id} className="p-5 flex items-center justify-between" data-testid={`concierge-${c.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-sm bg-[#A47E2A]/10 flex items-center justify-center"><Sparkles size={16} className="as-gold"/></div>
                    <div>
                      <div className="text-[#1A1D24]">{c.title}</div>
                      <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest mt-0.5">{c.category}</div>
                    </div>
                  </div>
                  <div className={`as-pill ${c.status === "Confirmed" ? "as-pill-green" : c.status === "New" ? "as-pill-muted" : "as-pill-orange"}`}>{c.status}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="as-card p-8 text-center text-[#756E64]">No concierge requests yet.</div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}
